import {
  Box,
  Checkbox,
  Collapse,
  Icon,
  ListItem,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { updateMyFleetVessel } from '../../../api/my-fleet';
import OverflowToolTip from '../../../common-components/overflow-tooltip/overflow-tooltip';
import { useAppDispatch } from '../../../hooks';
import {
  ActiveVesselOption,
  MyFleetVessel,
  VesselOption,
} from '../../../models/my-fleet.model';
import {
  getProVesselTypeLabel,
  getProVesselTypeSVG,
  ProAlert,
  ProVesselTypes,
} from '../../../models/pro.model';
import { updateVessel } from '../../../state/my-fleet/my-fleet-vessels.slice';
import MyFleetVesselItemButtons from '../my-fleet-vessel-item-buttons/my-fleet-vessel-item-buttons';
import EditVessel from './edit-vessel/edit-vessel';
import VesselProAlert from './pro-alert/pro-alert';

interface MyFleetVesselListItemProps {
  vessel: MyFleetVessel;
  onChange: (vesselImo: string, checked: boolean) => void;
  selectedImos: string[];
  deleteActive: boolean;
  downloadActive: boolean;
  setActiveVesselOption: (option: ActiveVesselOption) => void;
  setSelectedAlertId: (alert_id: string) => void;
  activeVesselOption: ActiveVesselOption;
  proAlert: ProAlert | null;
}

const ACTIONS_WIDTH = '5.8rem'; //  3.5rem for 1, 5.8rem for 2, 8rem for 3 buttons

function MyFleetVesselListItem({
  vessel,
  onChange,
  selectedImos,
  deleteActive,
  downloadActive,
  setActiveVesselOption,
  setSelectedAlertId,
  activeVesselOption,
  proAlert,
}: MyFleetVesselListItemProps) {
  const dispatch = useAppDispatch();
  const [showItemButtons, setShowItemButtons] = useState(true);

  const handleCollapseEntered = () => {
    setShowItemButtons(false);
  };

  const handleCollapseExit = () => {
    setShowItemButtons(true);
  };

  const isBeingEdited =
    activeVesselOption.type === VesselOption.Edit &&
    activeVesselOption.imo === vessel.imo;
  const alertOptionActive =
    activeVesselOption.type === VesselOption.Alert &&
    activeVesselOption.imo === vessel.imo;

  const onOptionSelection = (optionType: VesselOption) => {
    setActiveVesselOption({ type: optionType, imo: vessel.imo });
  };

  const onDeleteAlert = (alert_id: string) => {
    setSelectedAlertId(alert_id);
  };

  const finishOption = () => setActiveVesselOption({ type: null, imo: null });

  const handleItemClick = () => {
    if (deleteActive || downloadActive) {
      onChange(vessel.imo, !selectedImos.includes(vessel.imo));
    }
  };

  const submitEdit = (editPayload: MyFleetVessel) => {
    dispatch(updateVessel(editPayload));
    return updateMyFleetVessel(editPayload);
  };

  // Prevent bubbling when clicking directly on the checkbox/radio
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <ListItem
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          py: 1,
          px: 1.5,
          textAlign: 'left',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          borderTop: '1px solid',
          borderTopColor: 'background.default',
          pr: ACTIONS_WIDTH, // reserves space for the action buttons
          cursor: deleteActive || downloadActive ? 'pointer' : 'default',
        }}
        data-testid={`my-fleet-vessel-item-${vessel.imo}`}
        aria-label={`Select vessel ${vessel.name || 'No Name'}`}
        onClick={handleItemClick}
      >
        <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
          <Collapse
            in={deleteActive}
            orientation="horizontal"
            timeout={300}
            unmountOnExit
          >
            <Checkbox
              size="small"
              checked={selectedImos.includes(vessel.imo)}
              onChange={(e) => onChange(vessel.imo, e.target.checked)}
              data-testid={`my-fleet-vessel-checkbox-${vessel.imo}`}
              onClick={stopPropagation}
            />
          </Collapse>
          <Collapse
            in={downloadActive}
            orientation="horizontal"
            timeout={300}
            unmountOnExit
          >
            <Radio
              size="small"
              checked={selectedImos.includes(vessel.imo)}
              onChange={(e) => onChange(vessel.imo, e.target.checked)}
              data-testid={`my-fleet-vessel-radio-${vessel.imo}`}
              key={vessel.imo}
              onClick={stopPropagation}
            />
          </Collapse>
          <Tooltip
            title={getProVesselTypeLabel(
              vessel.vessel_type || ProVesselTypes.UNKNOWN
            )}
          >
            <Icon
              sx={{
                backgroundColor: 'text.primary',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              data-testid={`svg-icon-${getProVesselTypeLabel(
                vessel.vessel_type || ProVesselTypes.UNKNOWN
              )}`}
            >
              <img
                src={getProVesselTypeSVG(
                  vessel.vessel_type || ProVesselTypes.UNKNOWN
                )}
                alt={`${getProVesselTypeLabel(
                  vessel.vessel_type || ProVesselTypes.UNKNOWN
                )} vessel icon`}
              />
            </Icon>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'hidden', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              flexWrap: 'nowrap',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            <Collapse in={!isBeingEdited} timeout={400} unmountOnExit appear>
              <OverflowToolTip
                watchKey={vessel.vessel_id + (vessel.name || '')}
              >
                {vessel.name || 'Name Unknown'}
              </OverflowToolTip>
            </Collapse>
          </Box>
          <OverflowToolTip watchKey={vessel.vessel_id + (vessel.imo || '')}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                sx={{
                  fontSize: isBeingEdited ? '1.1rem' : '0.8rem',
                  fontWeight: isBeingEdited ? 600 : 400,
                  color: isBeingEdited ? 'text.primary' : 'text.secondary',
                  transition: 'font-size 0.4s, color 0.4s, font-weight 0.4s',
                  lineHeight: 1.2,
                  display: 'inline-block',
                }}
              >
                {vessel.imo && `IMO ${vessel.imo}`}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 300,
                  color: 'text.disabled',
                  marginLeft: 1,
                  opacity: isBeingEdited ? 1 : 0,
                  maxWidth: isBeingEdited ? 100 : 0,
                  transition: 'opacity 0.4s, max-width 0.4s',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.2,
                  display: 'inline-block',
                }}
                aria-hidden={!isBeingEdited}
              >
                (editing)
              </Typography>
            </Box>
          </OverflowToolTip>
        </Box>
      </ListItem>
      <Collapse in={!isBeingEdited} timeout={400} unmountOnExit appear>
        {!deleteActive && showItemButtons && (
          <MyFleetVesselItemButtons
            onOptionSelection={onOptionSelection}
            showMoreOptions={!(isBeingEdited || alertOptionActive)}
            showAlertToggle={
              !!proAlert && !(isBeingEdited || alertOptionActive)
            }
            proAlert={proAlert}
            onDeleteAlert={onDeleteAlert}
            vesselImo={vessel.imo}
          />
        )}
      </Collapse>
      <Collapse
        in={isBeingEdited}
        timeout={400}
        unmountOnExit
        appear
        onEntered={handleCollapseEntered}
        onExited={handleCollapseExit}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
            background: 'background.paper',
            borderRadius: 2,
            boxShadow: 2,
            mt: 1,
          }}
        >
          <EditVessel
            onSubmit={submitEdit}
            onCancel={finishOption}
            onFinish={finishOption}
            vessel={vessel}
          />
        </Box>
      </Collapse>
      <Collapse
        in={alertOptionActive}
        timeout={400}
        unmountOnExit
        appear
        onEntered={handleCollapseEntered}
        onExited={handleCollapseExit}
      >
        <Box
          sx={{
            px: 2,
            py: 2,
          }}
        >
          <VesselProAlert
            onCancel={finishOption}
            onFinish={finishOption}
            vessel={vessel}
            proAlert={proAlert}
          />
        </Box>
      </Collapse>
    </Box>
  );
}

export default MyFleetVesselListItem;
